import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  diagnosticOrderQueueApi, type PendingDiagnosticOrderRow,
  type DiagnosticDiscipline, type PendingDiagnosticOrderStatus,
  type HealNTrackQueueFilters,
} from "@/api/endpoints";
import { QueueFilterBar, emptyQueueFilters, toQueryFilters } from "@/components/QueueFilters";
import { formatDateTime, displayTimezoneLabel } from "@/shared/datetime";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { PatientSummaryByUhid } from "@/components/PatientSummary";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { StatusPill } from "@/components/StatusPill";

const DISCIPLINES: ("" | DiagnosticDiscipline)[] = ["", "Lab", "Radiology"];
// Partial = some tests made it into the dx_order, the rest are still unmapped in the catalog.
const STATUSES: ("" | PendingDiagnosticOrderStatus)[] =
  ["", "Ready", "Processing", "Partial", "Processed", "Cancelled"];

/**
 * HealNTrack diagnostics order queue — lab/radiology orders pushed from HealNTrack, waiting to be
 * turned into a diagnostic order + bill. "Process" resolves the patient, creates the dx_order
 * (BillFirst raises the invoice), and writes the "Ordered" status back to HealNTrack.
 */
export function DiagnosticOrderQueuePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canProcess = useAuth(s => s.hasPermission("diagnostics.order.create"));

  const [discipline, setDiscipline] = useState<"" | DiagnosticDiscipline>("");
  const [status, setStatus] = useState<"" | PendingDiagnosticOrderStatus>("Ready");
  const [filters, setFilters] = useState<HealNTrackQueueFilters>(emptyQueueFilters);
  const [detail, setDetail] = useState<PendingDiagnosticOrderRow | null>(null);

  // One fetch (all statuses for the chosen discipline + filters); KPIs + the grid derive from it so
  // the summary stays accurate no matter which status the user is viewing. Status is deliberately
  // NOT sent to the server — the KPI row must count every status in the current filter window.
  const applied = toQueryFilters(filters);
  const query = useQuery({
    queryKey: ["dx-order-queue", discipline, applied],
    queryFn: () => diagnosticOrderQueueApi.queue({ discipline: discipline || undefined, ...applied }),
    refetchInterval: 15000, // new orders surface within ~15s
  });

  const all = useMemo(() => query.data ?? [], [query.data]);
  const rows = useMemo(() => status ? all.filter(r => r.status === status) : all, [all, status]);
  const kpis = useMemo(() => {
    const by = (s: string) => all.filter(r => r.status === s);
    return {
      ready: by("Ready").length,
      processing: by("Processing").length,
      processed: by("Processed").length,
      partial: by("Partial").length,
      testsQueued: by("Ready").reduce((n, r) => n + (r.lineCount ?? 0), 0),
    };
  }, [all]);

  const processMut = useMutation({
    mutationFn: (id: string) => diagnosticOrderQueueApi.process(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dx-order-queue"] }),
    meta: { successMessage: "Order processed — diagnostic order created & billed." },
  });

  const columns: GridColDef<PendingDiagnosticOrderRow>[] = [
    { field: "orderedOn", headerName: "Ordered", width: 170,
      valueFormatter: (v: string) => formatDateTime(v) },
    { field: "discipline", headerName: "Discipline", width: 110 },
    { field: "externalOrderId", headerName: "HnT Order#", width: 110 },
    { field: "uhid", headerName: "Patient", width: 210, sortable: false,
      renderCell: (p) => (
        <Stack spacing={0} sx={{ lineHeight: 1.3, py: 0.5 }}>
          <Typography variant="body2">{p.row.patientName || p.row.uhid || `patient ${p.row.patientId}`}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.uhid ?? `#${p.row.patientId}`}{p.row.patientPhone ? ` · ${p.row.patientPhone}` : ""}
          </Typography>
        </Stack>
      ) },
    // Falls back to the id: orders received before HealNTrack started sending the name have none.
    { field: "orderByName", headerName: "Doctor", width: 180, sortable: false,
      renderCell: (p) => p.row.orderByName
        ? <span>{p.row.orderByName}</span>
        : <Box component="span" sx={{ color: "text.secondary" }}>#{p.row.orderBy}</Box> },
    // Spans, not Typography: the grid centres plain cell text with a row-tall line-height, and a
    // block element opts out of that line box — the count then sat high in the row, out of line
    // with every other column.
    { field: "lineCount", headerName: "Tests", width: 110, sortable: false,
      renderCell: (p) => {
        const done = p.row.processedLineCount, total = p.row.lineCount;
        // Before processing there is nothing done yet — show the plain count, not "0 / 4".
        if (done === 0) return <span>{total}</span>;
        return (
          <Box component="span" sx={{ color: done < total ? "warning.main" : "success.main" }}>
            {done} / {total}
          </Box>
        );
      } },
    { field: "isExternal", headerName: "Source", width: 100,
      renderCell: (p) => p.row.isExternal ? <StatusPill label="External" tone="warning" /> : null },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => <StatusPill label={p.row.status} /> },
    {
      field: "actions", headerName: "", width: 150, sortable: false, filterable: false, align: "right",
      renderCell: (p) => {
        if (p.row.status === "Processed" && p.row.diagnosticOrderId)
          return (
            <Button size="small" onClick={(e) => { e.stopPropagation(); navigate(`/diagnostics/orders`); }}>
              View order
            </Button>
          );
        if (p.row.status !== "Ready") return null;
        return (
          <Tooltip title={canProcess ? "Create diagnostic order + bill" : "You lack diagnostics.order.create"}>
            <span>
              <Button
                size="small" variant="contained" disabled={!canProcess || processMut.isPending}
                onClick={(e) => { e.stopPropagation(); processMut.mutate(p.row.id); }}
              >
                Process
              </Button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Diagnostics", "Order queue"]}
        title="Order queue"
        // The zone is named so nobody has to wonder whose clock the "Ordered" column is on.
        subtitle={`Lab & radiology orders received from HealNTrack, awaiting billing. `
          + `Times shown in ${displayTimezoneLabel()}.`}
        actions={
          canProcess ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate("/diagnostics/orders/new")}>
              New order
            </Button>
          ) : undefined
        }
      />

      {processMut.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(processMut.error)}</Alert>}

      {/* KPI summary — click a tile to filter the grid to that state. */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <StatCard label="Awaiting billing" value={kpis.ready} severity="info"
          hint="Ready to process" onClick={() => setStatus("Ready")} />
        <StatCard label="In processing" value={kpis.processing} severity="warning"
          onClick={() => setStatus("Processing")} />
        <StatCard label="Partly processed" value={kpis.partial} severity="warning"
          hint="Some tests still unmapped" onClick={() => setStatus("Partial")} />
        <StatCard label="Processed" value={kpis.processed} severity="success"
          onClick={() => setStatus("Processed")} />
        <StatCard label="Tests queued" value={kpis.testsQueued} hint="Across ready orders" />
      </Stack>

      <Paper sx={{ p: 2 }}>
        <QueueFilterBar
          value={filters} onChange={setFilters}
          leading={<>
            <TextField select label="Discipline" size="small" sx={{ minWidth: 150 }}
              value={discipline} onChange={(e) => setDiscipline(e.target.value as "" | DiagnosticDiscipline)}>
              {DISCIPLINES.map(d => <MenuItem key={d} value={d}>{d || "All"}</MenuItem>)}
            </TextField>
            <TextField select label="Status" size="small" sx={{ minWidth: 150 }}
              value={status} onChange={(e) => setStatus(e.target.value as "" | PendingDiagnosticOrderStatus)}>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
            </TextField>
          </>}
        />

        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          loading={query.isLoading}
          disableRowSelectionOnClick
          onRowClick={(p) => setDetail(p.row as PendingDiagnosticOrderRow)}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
          pageSizeOptions={[25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        />
      </Paper>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setDetail(null)}>
          {detail?.discipline} order · HnT #{detail?.externalOrderId}
        </ClosableDialogTitle>
        <DialogContent>
          {detail && (
            <>
              <Box sx={{ mb: 2 }}>
                {/* Queued orders carry no UHID until processed — resolve by HealNTrack patientid instead. */}
                <PatientSummaryByUhid
                  dense
                  uhid={detail.uhid}
                  patientId={detail.patientId}
                  fallbackName={`patient ${detail.patientId}`}
                />
              </Box>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Stack><Typography variant="caption" color="text.secondary">Discipline</Typography>
                  <Typography variant="body2">{detail.discipline}</Typography></Stack>
                <Stack><Typography variant="caption" color="text.secondary">Tests</Typography>
                  <Typography variant="body2">{detail.lineCount}</Typography></Stack>
                <Stack><Typography variant="caption" color="text.secondary">Ordered</Typography>
                  <Typography variant="body2">{formatDateTime(detail.orderedOn, "—")}</Typography></Stack>
                <Stack><Typography variant="caption" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}><StatusPill label={detail.status} /></Box></Stack>
                {detail.isExternal && (
                  <Stack><Typography variant="caption" color="text.secondary">Source</Typography>
                    <Box sx={{ mt: 0.5 }}><StatusPill label="External" tone="warning" /></Box></Stack>
                )}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
          {detail?.status === "Processed" && detail?.diagnosticOrderId && (
            <Button variant="contained" onClick={() => { setDetail(null); navigate("/diagnostics/orders"); }}>
              View order
            </Button>
          )}
          {detail?.status === "Ready" && canProcess && (
            <Button variant="contained" disabled={processMut.isPending}
              onClick={() => { const id = detail.id; setDetail(null); processMut.mutate(id); }}>
              Process
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
